import { INFO_FILE_PREFIX, INFO_MANIFEST, KEY_SIZE } from "./constants.js";
import { randomBytes, utf8 } from "./bytes.js";
import { LscError } from "./errors.js";

export type AesUsage = "encrypt" | "decrypt";

/** MK = random(32) */
export function generateMasterKey(): Uint8Array {
  return randomBytes(KEY_SIZE);
}

export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number,
): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey("raw", ikm, "HKDF", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "HKDF", hash: "SHA-256", salt, info }, base, length * 8);
  return new Uint8Array(bits);
}

/** §4 — K_manifest = HKDF(MK, salt = transferId, info = "LSC1/manifest") */
export async function deriveManifestKey(masterKey: Uint8Array, transferId: string): Promise<Uint8Array> {
  assertKey(masterKey);
  return hkdfSha256(masterKey, utf8(transferId), utf8(INFO_MANIFEST), KEY_SIZE);
}

/** §4 — K_file = HKDF(MK, salt = transferId, info = "LSC1/file/" || fileId) */
export async function deriveFileKey(masterKey: Uint8Array, transferId: string, fileId: string): Promise<Uint8Array> {
  assertKey(masterKey);
  return hkdfSha256(masterKey, utf8(transferId), utf8(INFO_FILE_PREFIX + fileId), KEY_SIZE);
}

export async function importAesKey(raw: Uint8Array, usages: AesUsage[] = ["encrypt", "decrypt"]): Promise<CryptoKey> {
  assertKey(raw);
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, usages);
}

export async function toAesKey(key: CryptoKey | Uint8Array, usages: AesUsage[]): Promise<CryptoKey> {
  return key instanceof Uint8Array ? importAesKey(key, usages) : key;
}

function assertKey(k: Uint8Array): void {
  if (!(k instanceof Uint8Array) || k.length !== KEY_SIZE) {
    throw new LscError("BAD_INPUT", `key must be ${KEY_SIZE} bytes`);
  }
}
